import useReveal from "@/hooks/useReveal";
import { Factory, MapPin, Layers, RefreshCw } from "lucide-react";

const PILLARS = [
  {
    icon: Factory,
    no: "I",
    title: "In-House Production",
    copy: "End-to-end production controlled by Auraa — design integrity, fabric sourcing and quality protocols held under one roof.",
  },
  {
    icon: MapPin,
    no: "II",
    title: "National Rollout",
    copy: "Coordinated dispatch and on-property fit-out across Indian cities. A single point of accountability for every site.",
  },
  {
    icon: Layers,
    no: "III",
    title: "Multi-Property Consistency",
    copy: "Programmes engineered for portfolios — unified palette and silhouette logic, adaptable to each property's accents.",
  },
  {
    icon: RefreshCw,
    no: "IV",
    title: "Repeat Replenishment",
    copy: "Long-horizon partnerships for new hires, seasonal additions and replacement cycles — without restarting the brief.",
  },
];

export default function Capability() {
  const eyebrowRef = useReveal();
  const headRef = useReveal();
  return (
    <section data-testid="capability-section" className="py-28 md:py-40 lg:py-48">
      <div className="container-luxury">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
          <div className="lg:col-span-7">
            <div ref={eyebrowRef} className="reveal flex items-center gap-4 mb-8">
              <span className="h-px w-10 bg-oxblood" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-oxblood font-medium">
                Operational Trust
              </span>
            </div>
            <h2
              ref={headRef}
              className="reveal reveal-delay-1 font-serif text-[40px] md:text-[56px] lg:text-[72px] leading-[1.04] tracking-[-0.015em] text-espresso text-balance"
            >
              Built for hospitality
              <br />
              <em className="italic text-oxblood">execution at scale.</em>
            </h2>
          </div>
          <RevealCopy />
        </div>

        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-px bg-borderwarm border border-borderwarm">
          {PILLARS.map((p, i) => (
            <Pillar key={p.title} p={p} delay={(i % 4) + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RevealCopy() {
  const ref = useReveal();
  return (
    <p
      ref={ref}
      className="reveal reveal-delay-2 lg:col-span-5 text-[15px] md:text-[17px] leading-[1.85] text-espresso-soft font-light max-w-md lg:justify-self-end"
    >
      Design integrity, met by operational discipline. Auraa runs uniform
      programmes for single boutique properties and multi-city hospitality
      portfolios alike.
    </p>
  );
}

function Pillar({ p, delay }) {
  const ref = useReveal();
  const Icon = p.icon;
  return (
    <article
      ref={ref}
      data-testid={`capability-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`reveal reveal-delay-${delay} bg-bone p-10 md:p-14 lg:p-16 group flex flex-col lg:flex-row gap-8 lg:gap-12 relative`}
    >
      <div className="shrink-0 flex lg:flex-col items-start lg:items-start gap-6 lg:gap-8">
        <span className="font-serif text-[28px] text-champagne-dark italic">{p.no}</span>
        <span className="h-px w-12 bg-borderwarm group-hover:bg-oxblood group-hover:w-20 transition-all duration-700" />
      </div>

      <div className="flex-1">
        <Icon size={22} strokeWidth={1.25} className="text-oxblood mb-6" />
        <h3 className="font-serif text-[26px] md:text-[30px] lg:text-[34px] tracking-tight text-espresso leading-[1.1]">
          {p.title}
        </h3>
        <p className="mt-4 text-[15px] md:text-[16px] leading-relaxed text-espresso-soft font-light max-w-md">
          {p.copy}
        </p>
      </div>
    </article>
  );
}
